import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Calendar, Megaphone, Image, Settings, Shield, ChevronLeft,
  Plus, Trash2, Pencil, Save, X, Loader2, Upload, Film, Camera,
  Video, UserPlus, Trophy, Clock, MapPin, Hash, Play,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'teams', label: 'Teams', icon: Shield },
  { id: 'roster', label: 'Roster', icon: Users },
  { id: 'coaches', label: 'Coaches', icon: UserPlus },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
  { id: 'media', label: 'Media', icon: Image },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const formatTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

const ProgramAdmin = () => {
  const { slug } = useParams();
  const { token, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('teams');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  const color = program?.primary_color || '#06b6d4';

  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/programs/by-slug/${slug}`);
        if (!res.ok) { navigate('/teams'); return; }
        const prog = await res.json();
        setProgram(prog);

        // Check access
        if (token) {
          const myRes = await fetch(`${API_URL}/api/programs/my-programs`, { headers: { Authorization: `Bearer ${token}` } });
          if (myRes.ok) {
            const myProgs = await myRes.json();
            const mine = myProgs.find(p => p.id === prog.id);
            const role = mine?.my_role || (['admin', 'super_admin'].includes(user?.role) ? 'super_admin' : null);
            if (!role || !['program_admin', 'super_admin'].includes(role)) {
              toast.error('Access denied');
              navigate(`/${slug}/dashboard`);
              return;
            }
          }
        } else {
          navigate('/login');
          return;
        }
      } catch { navigate('/teams'); }
      setLoading(false);
    };
    load();
  }, [slug, token, authLoading]);

  if (loading || !program) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-black" data-testid="program-admin-page">
      {/* Top Bar */}
      <div className="border-b border-zinc-800/60 bg-zinc-950/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={`/${slug}/dashboard`} className="text-zinc-500 hover:text-white text-xs flex items-center gap-1" data-testid="back-link">
              <ChevronLeft className="w-4 h-4" /> Dashboard
            </Link>
            <div className="w-px h-4 bg-zinc-800" />
            <h1 className="text-white font-bold text-base">Admin Panel</h1>
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-zinc-400 border-zinc-700">{program.name}</Badge>
          </div>
          {/* Mobile menu toggle */}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden text-zinc-400 hover:text-white">
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block w-full lg:w-48 shrink-0 border-r border-zinc-800/60 bg-zinc-950/50 lg:min-h-[calc(100vh-53px)]`}>
          <nav className="p-3 space-y-0.5" data-testid="admin-sidebar">
            {SECTIONS.map(s => {
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => { setSection(s.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    section === s.id ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900'
                  }`}
                  data-testid={`nav-${s.id}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-6 min-h-[calc(100vh-53px)]">
          {section === 'teams' && <TeamsSection program={program} headers={headers} token={token} color={color} />}
          {section === 'roster' && <RosterSection program={program} headers={headers} token={token} color={color} />}
          {section === 'coaches' && <CoachesSection program={program} headers={headers} token={token} color={color} />}
          {section === 'schedule' && <ScheduleSection program={program} headers={headers} token={token} color={color} />}
          {section === 'announcements' && <AnnouncementsSection program={program} headers={headers} token={token} color={color} />}
          {section === 'media' && <MediaSection program={program} headers={headers} token={token} color={color} slug={slug} />}
          {section === 'settings' && <SettingsSection program={program} setProgram={setProgram} headers={headers} token={token} color={color} />}
        </div>
      </div>
    </div>
  );
};


/* ========== SECTION WRAPPER ========== */
const SectionHeader = ({ icon: Icon, title, action, color }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-white font-bold text-lg flex items-center gap-2">
      <Icon className="w-5 h-5" style={{ color }} /> {title}
    </h2>
    {action}
  </div>
);


/* ========== TEAMS SECTION ========== */
const TeamsSection = ({ program, headers, color }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', age_group: '', level: '', division: '', team_color: '#06b6d4' });

  const load = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/programs/${program.id}/teams`);
    if (res.ok) setTeams(await res.json());
    setLoading(false);
  }, [program.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const url = editId
      ? `${API_URL}/api/programs/${program.id}/teams/${editId}`
      : `${API_URL}/api/programs/${program.id}/teams`;
    const res = await fetch(url, {
      method: editId ? 'PUT' : 'POST', headers, body: JSON.stringify(form),
    });
    if (res.ok) { toast.success(editId ? 'Team updated' : 'Team created'); setShowForm(false); setEditId(null); load(); }
    else toast.error('Failed to save');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this team?')) return;
    await fetch(`${API_URL}/api/programs/${program.id}/teams/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted'); load();
  };

  const startEdit = (t) => {
    setForm({ name: t.name, age_group: t.age_group || '', level: t.level || '', division: t.division || '', team_color: t.team_color || '#06b6d4' });
    setEditId(t.id); setShowForm(true);
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto mt-10" />;

  return (
    <div data-testid="teams-section">
      <SectionHeader icon={Shield} title="Teams" color={color} action={
        <Button onClick={() => { setForm({ name: '', age_group: '', level: '', division: '', team_color: '#06b6d4' }); setEditId(null); setShowForm(true); }} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="add-team-btn">
          <Plus className="w-3.5 h-3.5" /> Add Team
        </Button>
      } />

      {/* Team form */}
      {showForm && (
        <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="team-form">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Team Name *" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="team-name-input" />
              <Input value={form.age_group} onChange={e => setForm(p => ({ ...p, age_group: e.target.value }))} placeholder="Age Group (e.g. 14U)" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="team-age-input" />
              <select value={form.level} onChange={e => setForm(p => ({ ...p, level: e.target.value }))} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-md px-3 py-2" data-testid="team-level-select">
                <option value="">Level</option>
                <option value="Gold">Gold</option><option value="Silver">Silver</option><option value="Bronze">Bronze</option>
              </select>
              <Input value={form.division} onChange={e => setForm(p => ({ ...p, division: e.target.value }))} placeholder="Division (e.g. East)" className="bg-zinc-800 border-zinc-700 text-white text-xs" />
              <div className="flex items-center gap-2">
                <input type="color" value={form.team_color} onChange={e => setForm(p => ({ ...p, team_color: e.target.value }))} className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent" data-testid="team-color-picker" />
                <span className="text-zinc-500 text-xs">Team Color</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="team-save-btn"><Save className="w-3 h-3" /> {editId ? 'Update' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="text-xs border-zinc-700"><X className="w-3 h-3" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team list */}
      <div className="space-y-2">
        {teams.map(t => (
          <div key={t.id} className="flex items-center gap-3 bg-zinc-900/60 border border-zinc-800/60 rounded-xl px-4 py-3" data-testid={`team-row-${t.id}`}>
            <div className="w-3 h-8 rounded-full shrink-0" style={{ backgroundColor: t.team_color || color }} />
            <div className="flex-1 min-w-0">
              <span className="text-white text-sm font-medium">{t.name}</span>
              <div className="flex items-center gap-2 text-[10px] text-zinc-500 mt-0.5">
                {t.age_group && <Badge variant="outline" className="text-[8px] px-1 py-0 border-zinc-700">{t.age_group}</Badge>}
                {t.level && <span>{t.level}</span>}
                {t.division && <span>{t.division}</span>}
                <span>Record: {t.record || '0-0'}</span>
              </div>
            </div>
            <button onClick={() => startEdit(t)} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-white" data-testid={`edit-team-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={() => remove(t.id)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-zinc-500 hover:text-red-400" data-testid={`delete-team-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {teams.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No teams yet</p>}
      </div>
    </div>
  );
};


/* ========== ROSTER SECTION ========== */
const RosterSection = ({ program, headers, color }) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', role: 'parent', jersey_number: '', position: '', email: '' });

  useEffect(() => {
    fetch(`${API_URL}/api/programs/${program.id}/teams`).then(r => r.json()).then(setTeams).catch(() => {});
  }, [program.id]);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    const params = selectedTeam ? `?role=parent&team_id=${selectedTeam}` : '?role=parent';
    const res = await fetch(`${API_URL}/api/programs/${program.id}/members${params}`, { headers });
    if (res.ok) setMembers(await res.json());
    setLoading(false);
  }, [program.id, selectedTeam, headers]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const save = async () => {
    const body = { ...form, team_ids: selectedTeam ? [selectedTeam] : [] };
    const url = editId
      ? `${API_URL}/api/programs/${program.id}/members/${editId}`
      : `${API_URL}/api/programs/${program.id}/members`;
    const res = await fetch(url, { method: editId ? 'PUT' : 'POST', headers, body: JSON.stringify(body) });
    if (res.ok) { toast.success(editId ? 'Player updated' : 'Player added'); setShowForm(false); setEditId(null); loadMembers(); }
    else toast.error('Failed to save');
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this player?')) return;
    await fetch(`${API_URL}/api/programs/${program.id}/members/${id}`, { method: 'DELETE', headers });
    toast.success('Removed'); loadMembers();
  };

  const startEdit = (m) => {
    setForm({ name: m.name, role: m.role || 'parent', jersey_number: m.jersey_number || '', position: m.position || '', email: m.email || '' });
    setEditId(m.id); setShowForm(true);
  };

  return (
    <div data-testid="roster-section">
      <SectionHeader icon={Users} title="Roster" color={color} action={
        <Button onClick={() => { setForm({ name: '', role: 'parent', jersey_number: '', position: '', email: '' }); setEditId(null); setShowForm(true); }} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="add-player-btn">
          <Plus className="w-3.5 h-3.5" /> Add Player
        </Button>
      } />

      {/* Team filter */}
      <select value={selectedTeam} onChange={e => setSelectedTeam(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-lg px-3 py-2 mb-4 outline-none" data-testid="roster-team-filter">
        <option value="">All Teams</option>
        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>

      {showForm && (
        <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="roster-form">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Player Name *" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="player-name-input" />
              <Input value={form.jersey_number} onChange={e => setForm(p => ({ ...p, jersey_number: e.target.value }))} placeholder="Jersey #" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="player-jersey-input" />
              <Input value={form.position} onChange={e => setForm(p => ({ ...p, position: e.target.value }))} placeholder="Position" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="player-position-input" />
              <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email (optional)" className="bg-zinc-800 border-zinc-700 text-white text-xs" />
            </div>
            <div className="flex gap-2">
              <Button onClick={save} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="roster-save-btn"><Save className="w-3 h-3" /> {editId ? 'Update' : 'Add'}</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="text-xs border-zinc-700"><X className="w-3 h-3" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto mt-8" /> : (
        <div className="space-y-1.5">
          {members.filter(m => m.role === 'parent').map(m => (
            <div key={m.id} className="flex items-center gap-3 bg-zinc-900/60 border border-zinc-800/60 rounded-xl px-4 py-2.5" data-testid={`roster-row-${m.id}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs" style={{ backgroundColor: `${color}20`, color }}>
                {m.jersey_number || '#'}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-white text-sm font-medium">{m.name}</span>
                <div className="text-[10px] text-zinc-500">{m.position || 'No position'} {m.email ? `· ${m.email}` : ''}</div>
              </div>
              <button onClick={() => startEdit(m)} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(m.id)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {members.filter(m => m.role === 'parent').length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No players on this roster</p>}
        </div>
      )}
    </div>
  );
};


/* ========== COACHES SECTION ========== */
const CoachesSection = ({ program, headers, color }) => {
  const [teams, setTeams] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', team_ids: [] });

  useEffect(() => {
    fetch(`${API_URL}/api/programs/${program.id}/teams`).then(r => r.json()).then(setTeams).catch(() => {});
  }, [program.id]);

  const loadCoaches = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/api/programs/${program.id}/members?role=coach`, { headers });
    if (res.ok) setCoaches(await res.json());
    setLoading(false);
  }, [program.id, headers]);

  useEffect(() => { loadCoaches(); }, [loadCoaches]);

  const save = async () => {
    const body = { ...form, role: 'coach' };
    const res = await fetch(`${API_URL}/api/programs/${program.id}/members`, { method: 'POST', headers, body: JSON.stringify(body) });
    if (res.ok) { toast.success('Coach added'); setShowForm(false); loadCoaches(); }
    else toast.error('Failed to add');
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this coach?')) return;
    await fetch(`${API_URL}/api/programs/${program.id}/members/${id}`, { method: 'DELETE', headers });
    toast.success('Removed'); loadCoaches();
  };

  const toggleTeam = (teamId) => {
    setForm(p => ({
      ...p,
      team_ids: p.team_ids.includes(teamId) ? p.team_ids.filter(t => t !== teamId) : [...p.team_ids, teamId],
    }));
  };

  return (
    <div data-testid="coaches-section">
      <SectionHeader icon={UserPlus} title="Coaches" color={color} action={
        <Button onClick={() => { setForm({ name: '', email: '', team_ids: [] }); setShowForm(true); }} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="add-coach-btn">
          <Plus className="w-3.5 h-3.5" /> Add Coach
        </Button>
      } />

      {showForm && (
        <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="coach-form">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Coach Name *" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="coach-name-input" />
              <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="coach-email-input" />
            </div>
            <div>
              <span className="text-zinc-500 text-[11px] font-medium mb-1 block">Assign to Teams:</span>
              <div className="flex flex-wrap gap-1.5">
                {teams.map(t => (
                  <button key={t.id} onClick={() => toggleTeam(t.id)} className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-colors ${form.team_ids.includes(t.id) ? 'border-cyan-700 bg-cyan-500/10 text-cyan-400' : 'border-zinc-800 bg-zinc-900 text-zinc-500'}`} data-testid={`assign-team-${t.id}`}>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="coach-save-btn"><Save className="w-3 h-3" /> Add Coach</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="text-xs border-zinc-700"><X className="w-3 h-3" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto mt-8" /> : (
        <div className="space-y-1.5">
          {coaches.map(c => (
            <div key={c.id} className="flex items-center gap-3 bg-zinc-900/60 border border-zinc-800/60 rounded-xl px-4 py-2.5" data-testid={`coach-row-${c.id}`}>
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-cyan-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-white text-sm font-medium">{c.name}</span>
                <div className="text-[10px] text-zinc-500">{c.email || 'No email'} · {(c.team_ids || []).length} team(s)</div>
              </div>
              <button onClick={() => remove(c.id)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {coaches.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No coaches assigned</p>}
        </div>
      )}
    </div>
  );
};


/* ========== SCHEDULE SECTION ========== */
const ScheduleSection = ({ program, headers, color }) => {
  const [teams, setTeams] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', event_type: 'game', team_id: '', start_time: '', location: '', opponent: '', status: 'scheduled' });

  useEffect(() => {
    fetch(`${API_URL}/api/programs/${program.id}/teams`).then(r => r.json()).then(setTeams).catch(() => {});
  }, [program.id]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/api/programs/${program.id}/schedule?limit=50`);
    if (res.ok) setEvents(await res.json());
    setLoading(false);
  }, [program.id]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const save = async () => {
    const url = editId
      ? `${API_URL}/api/programs/${program.id}/schedule/${editId}`
      : `${API_URL}/api/programs/${program.id}/schedule`;
    const res = await fetch(url, { method: editId ? 'PUT' : 'POST', headers, body: JSON.stringify(form) });
    if (res.ok) { toast.success(editId ? 'Event updated' : 'Event created'); setShowForm(false); setEditId(null); loadEvents(); }
    else toast.error('Failed to save');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await fetch(`${API_URL}/api/programs/${program.id}/schedule/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted'); loadEvents();
  };

  const startEdit = (e) => {
    setForm({
      title: e.title, event_type: e.event_type, team_id: e.team_id || '',
      start_time: e.start_time?.slice(0, 16) || '', location: e.location || '',
      opponent: e.opponent || '', status: e.status || 'scheduled',
    });
    setEditId(e.id); setShowForm(true);
  };

  const typeColors = { game: 'text-red-400 border-red-800', tournament: 'text-amber-400 border-amber-800', practice: 'text-emerald-400 border-emerald-800' };

  return (
    <div data-testid="schedule-section">
      <SectionHeader icon={Calendar} title="Schedule" color={color} action={
        <Button onClick={() => { setForm({ title: '', event_type: 'game', team_id: '', start_time: '', location: '', opponent: '', status: 'scheduled' }); setEditId(null); setShowForm(true); }} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="add-event-btn">
          <Plus className="w-3.5 h-3.5" /> Add Event
        </Button>
      } />

      {showForm && (
        <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="schedule-form">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Event Title *" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-title-input" />
              <select value={form.event_type} onChange={e => setForm(p => ({ ...p, event_type: e.target.value }))} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-md px-3 py-2" data-testid="event-type-select">
                <option value="game">Game</option><option value="tournament">Tournament</option><option value="practice">Practice</option><option value="film_session">Film Session</option><option value="other">Other</option>
              </select>
              <select value={form.team_id} onChange={e => setForm(p => ({ ...p, team_id: e.target.value }))} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-md px-3 py-2" data-testid="event-team-select">
                <option value="">No team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <Input type="datetime-local" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-datetime-input" />
              <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Location" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-location-input" />
              <Input value={form.opponent} onChange={e => setForm(p => ({ ...p, opponent: e.target.value }))} placeholder="Opponent" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="event-opponent-input" />
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-md px-3 py-2" data-testid="event-status-select">
                <option value="scheduled">Scheduled</option><option value="live">Live</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="event-save-btn"><Save className="w-3 h-3" /> {editId ? 'Update' : 'Create'}</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="text-xs border-zinc-700"><X className="w-3 h-3" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto mt-8" /> : (
        <div className="space-y-1.5">
          {events.map(ev => (
            <div key={ev.id} className="flex items-center gap-3 bg-zinc-900/60 border border-zinc-800/60 rounded-xl px-4 py-2.5" data-testid={`event-row-${ev.id}`}>
              <div className="text-center shrink-0 w-10">
                <span className="text-white font-bold text-sm block">{ev.start_time ? new Date(ev.start_time).getDate() : '?'}</span>
                <span className="text-zinc-500 text-[9px] uppercase">{ev.start_time ? new Date(ev.start_time).toLocaleString('en', { month: 'short' }) : ''}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium">{ev.title}</span>
                  <Badge variant="outline" className={`text-[8px] px-1 py-0 ${typeColors[ev.event_type] || 'text-zinc-500 border-zinc-700'}`}>{ev.event_type}</Badge>
                  {ev.status === 'cancelled' && <Badge className="text-[8px] px-1 py-0 bg-red-500/15 text-red-400 border border-red-800">Cancelled</Badge>}
                </div>
                <div className="text-[10px] text-zinc-500 flex items-center gap-2 mt-0.5">
                  {ev.start_time && <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> {formatTime(ev.start_time)}</span>}
                  {ev.location && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" /> {ev.location}</span>}
                  {ev.opponent && <span>vs {ev.opponent}</span>}
                </div>
              </div>
              <button onClick={() => startEdit(ev)} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(ev.id)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {events.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No events scheduled</p>}
        </div>
      )}
    </div>
  );
};


/* ========== ANNOUNCEMENTS SECTION ========== */
const AnnouncementsSection = ({ program, headers, color }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', pinned: true, priority: 'normal' });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/api/programs/${program.id}/announcements?limit=30`);
    if (res.ok) setAnnouncements(await res.json());
    setLoading(false);
  }, [program.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const res = await fetch(`${API_URL}/api/programs/${program.id}/announcements`, { method: 'POST', headers, body: JSON.stringify(form) });
    if (res.ok) { toast.success('Announcement posted'); setShowForm(false); load(); }
    else toast.error('Failed to post');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    await fetch(`${API_URL}/api/programs/${program.id}/announcements/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted'); load();
  };

  return (
    <div data-testid="announcements-section">
      <SectionHeader icon={Megaphone} title="Announcements" color={color} action={
        <Button onClick={() => { setForm({ title: '', content: '', pinned: true, priority: 'normal' }); setShowForm(true); }} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="add-announcement-btn">
          <Plus className="w-3.5 h-3.5" /> New Announcement
        </Button>
      } />

      {showForm && (
        <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="announcement-form">
          <CardContent className="p-4 space-y-3">
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Title *" className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="announcement-title-input" />
            <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="Content *" className="w-full bg-zinc-800 border border-zinc-700 text-white text-xs rounded-md px-3 py-2 min-h-[80px] outline-none resize-none" data-testid="announcement-content-input" />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 text-xs text-zinc-400">
                <input type="checkbox" checked={form.pinned} onChange={e => setForm(p => ({ ...p, pinned: e.target.checked }))} className="rounded" data-testid="announcement-pinned-check" /> Pinned
              </label>
              <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-md px-2 py-1" data-testid="announcement-priority-select">
                <option value="normal">Normal</option><option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="announcement-save-btn"><Save className="w-3 h-3" /> Post</Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="text-xs border-zinc-700"><X className="w-3 h-3" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto mt-8" /> : (
        <div className="space-y-1.5">
          {announcements.map(a => (
            <div key={a.id} className={`flex items-start gap-3 bg-zinc-900/60 border rounded-xl px-4 py-3 ${a.priority === 'urgent' ? 'border-amber-800/60' : 'border-zinc-800/60'}`} data-testid={`announcement-row-${a.id}`}>
              <Megaphone className={`w-4 h-4 mt-0.5 shrink-0 ${a.priority === 'urgent' ? 'text-amber-400' : 'text-zinc-500'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-medium">{a.title}</span>
                  {a.pinned && <Badge variant="outline" className="text-[8px] px-1 py-0 border-zinc-700 text-zinc-400">Pinned</Badge>}
                  {a.priority === 'urgent' && <Badge className="text-[8px] px-1 py-0 bg-amber-500/15 text-amber-400 border border-amber-800">Urgent</Badge>}
                </div>
                <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{a.content}</p>
                <span className="text-[10px] text-zinc-600 mt-1 block">By {a.author_name} · {formatDate(a.created_at)}</span>
              </div>
              <button onClick={() => remove(a.id)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {announcements.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No announcements</p>}
        </div>
      )}
    </div>
  );
};


/* ========== MEDIA SECTION ========== */
const MediaSection = ({ program, headers, token, color, slug }) => {
  const [media, setMedia] = useState([]);
  const [teams, setTeams] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadForm, setUploadForm] = useState({ title: '', team_id: '', event_id: '', category: 'photo' });
  const [uploading, setUploading] = useState(false);
  const [ingesting, setIngesting] = useState(false);

  const CATEGORY_META = {
    highlight: { label: 'Highlight', cls: 'text-amber-400 border-amber-800' },
    full_game: { label: 'Full Game', cls: 'text-cyan-400 border-cyan-800' },
    photo: { label: 'Photo', cls: 'text-emerald-400 border-emerald-800' },
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [mediaRes, teamsRes, eventsRes] = await Promise.all([
      fetch(`${API_URL}/api/programs/${program.id}/media-feed?limit=50`),
      fetch(`${API_URL}/api/programs/${program.id}/teams`),
      fetch(`${API_URL}/api/programs/${program.id}/schedule?limit=50`),
    ]);
    if (mediaRes.ok) { const d = await mediaRes.json(); setMedia(d.media || []); }
    if (teamsRes.ok) setTeams(await teamsRes.json());
    if (eventsRes.ok) setEvents(await eventsRes.json());
    setLoading(false);
  }, [program.id]);

  useEffect(() => { load(); }, [load]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setUploadForm(p => ({ ...p, category: f.type.startsWith('video/') ? 'highlight' : 'photo' }));
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else { setPreview(null); }
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const upRes = await fetch(`${API_URL}/api/uploads/program-media/${program.id}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      if (!upRes.ok) throw new Error('Upload failed');
      const upData = await upRes.json();

      const mediaRes = await fetch(`${API_URL}/api/programs/${program.id}/media`, {
        method: 'POST', headers,
        body: JSON.stringify({
          title: uploadForm.title || file.name.replace(/\.[^/.]+$/, ''),
          team_id: uploadForm.team_id || null,
          event_id: uploadForm.event_id || null,
          media_type: upData.media_type, category: uploadForm.category,
          url: upData.url,
          thumbnail_url: upData.media_type === 'photo' ? upData.url : null,
        }),
      });
      if (mediaRes.ok) {
        toast.success('Media uploaded');
        setShowUpload(false); setFile(null); setPreview(null); load();
      }
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this media?')) return;
    await fetch(`${API_URL}/api/programs/${program.id}/media/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted'); load();
  };

  const ingestVods = async () => {
    setIngesting(true);
    try {
      const res = await fetch(`${API_URL}/api/programs/${program.id}/ingest-vods`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Ingested ${d.imported} VODs from ${d.scanned} completed games`);
        if (d.imported > 0) load();
      } else toast.error('Ingestion failed');
    } catch { toast.error('Failed'); }
    setIngesting(false);
  };

  return (
    <div data-testid="media-section">
      <SectionHeader icon={Image} title="Media" color={color} action={
        <div className="flex gap-2">
          <Button onClick={ingestVods} variant="outline" className="text-xs gap-1.5 border-zinc-700 text-zinc-400" disabled={ingesting} data-testid="ingest-vods-btn">
            {ingesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />} Import VODs
          </Button>
          <Button onClick={() => setShowUpload(true)} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="upload-media-admin-btn">
            <Upload className="w-3.5 h-3.5" /> Upload
          </Button>
        </div>
      } />

      {/* Upload form */}
      {showUpload && (
        <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="media-upload-form">
          <CardContent className="p-4 space-y-3">
            {!file ? (
              <label className="flex flex-col items-center py-6 border-2 border-dashed border-zinc-800 rounded-xl cursor-pointer hover:border-zinc-600">
                <Upload className="w-6 h-6 text-zinc-600 mb-1" />
                <span className="text-zinc-400 text-xs">Select file</span>
                <input type="file" className="hidden" accept="image/*,video/*" onChange={handleFile} data-testid="admin-file-input" />
              </label>
            ) : (
              <div className="flex items-center gap-3">
                {preview ? <img src={preview} alt="" className="w-16 h-10 object-cover rounded border border-zinc-800" /> : <Video className="w-8 h-8 text-zinc-500" />}
                <span className="text-zinc-300 text-xs flex-1 truncate">{file.name}</span>
                <button onClick={() => { setFile(null); setPreview(null); }}><X className="w-4 h-4 text-zinc-500" /></button>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Input value={uploadForm.title} onChange={e => setUploadForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="bg-zinc-800 border-zinc-700 text-white text-xs" />
              <select value={uploadForm.team_id} onChange={e => setUploadForm(p => ({ ...p, team_id: e.target.value }))} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-md px-3 py-2">
                <option value="">No team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select value={uploadForm.event_id} onChange={e => setUploadForm(p => ({ ...p, event_id: e.target.value }))} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-md px-3 py-2">
                <option value="">No event</option>
                {events.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
              </select>
              <select value={uploadForm.category} onChange={e => setUploadForm(p => ({ ...p, category: e.target.value }))} className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-md px-3 py-2">
                <option value="photo">Photo</option><option value="highlight">Highlight</option><option value="full_game">Full Game</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={upload} disabled={!file || uploading} className="text-xs gap-1.5" style={{ backgroundColor: color }}>
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} {uploading ? 'Uploading...' : 'Upload'}
              </Button>
              <Button variant="outline" onClick={() => { setShowUpload(false); setFile(null); setPreview(null); }} className="text-xs border-zinc-700"><X className="w-3 h-3" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto mt-8" /> : (
        <div className="space-y-1.5">
          {media.map(m => {
            const cat = CATEGORY_META[m.category] || CATEGORY_META.photo;
            return (
              <div key={m.id} className="flex items-center gap-3 bg-zinc-900/60 border border-zinc-800/60 rounded-xl px-4 py-2.5" data-testid={`media-row-${m.id}`}>
                <div className="w-12 h-8 rounded bg-zinc-800 overflow-hidden shrink-0 flex items-center justify-center">
                  {m.thumbnail_url ? <img src={m.thumbnail_url?.startsWith('http') ? m.thumbnail_url : `${API_URL}${m.thumbnail_url}`} alt="" className="w-full h-full object-cover" /> : m.media_type === 'video' ? <Play className="w-4 h-4 text-zinc-600" /> : <Camera className="w-4 h-4 text-zinc-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium truncate">{m.title || 'Untitled'}</span>
                    <Badge variant="outline" className={`text-[8px] px-1 py-0 ${cat.cls}`}>{cat.label}</Badge>
                    {m.source === 'auto_vod_ingestion' && <Badge variant="outline" className="text-[8px] px-1 py-0 text-purple-400 border-purple-800">Auto-VOD</Badge>}
                  </div>
                  <div className="text-[10px] text-zinc-500">{m.team_name || 'Program-wide'} {m.event_name ? `· ${m.event_name}` : ''} · {formatDate(m.created_at)}</div>
                </div>
                <button onClick={() => remove(m.id)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
          {media.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No media</p>}
        </div>
      )}
    </div>
  );
};


/* ========== SETTINGS SECTION ========== */
const SettingsSection = ({ program, setProgram, headers, color }) => {
  const [form, setForm] = useState({
    name: program.name || '',
    description: program.description || '',
    primary_color: program.primary_color || '#06b6d4',
    secondary_color: program.secondary_color || '#0e1117',
    logo_url: program.logo_url || '',
    banner_url: program.banner_url || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const res = await fetch(`${API_URL}/api/programs/${program.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
    if (res.ok) {
      const updated = await res.json();
      setProgram(updated);
      toast.success('Settings saved');
    } else toast.error('Failed to save');
    setSaving(false);
  };

  return (
    <div data-testid="settings-section">
      <SectionHeader icon={Settings} title="Settings" color={color} />

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4 space-y-4">
          <div>
            <label className="text-zinc-400 text-[11px] font-medium block mb-1">Program Name</label>
            <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="settings-name-input" />
          </div>
          <div>
            <label className="text-zinc-400 text-[11px] font-medium block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 text-white text-xs rounded-md px-3 py-2 min-h-[60px] outline-none resize-none" data-testid="settings-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-[11px] font-medium block mb-1">Primary Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent" data-testid="settings-primary-color" />
                <Input value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white text-xs flex-1" />
              </div>
            </div>
            <div>
              <label className="text-zinc-400 text-[11px] font-medium block mb-1">Secondary Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.secondary_color} onChange={e => setForm(p => ({ ...p, secondary_color: e.target.value }))} className="w-8 h-8 rounded border-0 cursor-pointer bg-transparent" data-testid="settings-secondary-color" />
                <Input value={form.secondary_color} onChange={e => setForm(p => ({ ...p, secondary_color: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white text-xs flex-1" />
              </div>
            </div>
          </div>
          <div>
            <label className="text-zinc-400 text-[11px] font-medium block mb-1">Logo URL</label>
            <Input value={form.logo_url} onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="settings-logo-input" />
          </div>
          <div>
            <label className="text-zinc-400 text-[11px] font-medium block mb-1">Banner Image URL</label>
            <Input value={form.banner_url} onChange={e => setForm(p => ({ ...p, banner_url: e.target.value }))} placeholder="https://..." className="bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="settings-banner-input" />
          </div>

          {/* Preview */}
          <div className="pt-3 border-t border-zinc-800">
            <span className="text-zinc-500 text-[11px] font-medium mb-2 block">Preview</span>
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: form.secondary_color || '#0e1117' }}>
              {form.logo_url && <img src={form.logo_url} alt="" className="w-8 h-8 rounded object-cover" />}
              <span className="font-bold text-sm" style={{ color: form.primary_color || '#06b6d4' }}>{form.name}</span>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="text-xs gap-1.5" style={{ backgroundColor: color }} data-testid="settings-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};


export default ProgramAdmin;
